import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TaskItem, TaskList } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { userName } from '../../utils/taskUsers';
import { childrenOf } from '../../utils/taskTree';
import { inDaysLabel, lateCount, nextMarker, projectProgress } from './projectStats';
import ProgressCheck from '../tasks/ProgressCheck';
import TaskIcon from '../tasks/TaskIcon';
import { UserBubble } from '../tasks/UserBubble';

const SHOW_BRANCHES = 6;

/** A project on the Projects page: its main branches at a glance, like a list card. */
const ProjectCard: React.FC<{ project: TaskList; items: TaskItem[]; members: string[]; archived?: boolean; onRestore?: () => void }> = ({
  project,
  items,
  members,
  archived = false,
  onRestore,
}) => {
  const { users, me, today } = useTasks();
  const kids = useMemo(() => childrenOf(items), [items]);
  const branches = kids.get(null) || [];
  const open = items.filter((i) => i.progress < 100);
  const mine = open.filter((i) => i.assignees.includes(me)).length;
  const progress = projectProgress(items);
  const next = nextMarker(items, today);
  const late = lateCount(items);
  const people = [project.owner_email, ...members.filter((m) => m !== project.owner_email)];

  return (
    <section
      className={`tl-card tp-card${archived ? ' tl-card-readonly' : ''}`}
      style={project.color ? ({ '--tl-tint': project.color } as React.CSSProperties) : undefined}
    >
      <header className="tl-card-head">
        <div className="tl-card-title-wrap">
          <TaskIcon name="project" size={15} className="tp-card-icon" />
          {archived ? (
            <h2 className="tl-card-title tl-card-title-static">{project.title || 'Untitled project'}</h2>
          ) : (
            <Link to={`/projects/${project.id}`} className="tl-card-title tl-card-title-static tp-card-link">
              {project.title || 'Untitled project'}
            </Link>
          )}
        </div>
        {people.length > 1 && (
          <span className="tl-members" title={people.map((m) => userName(users, m)).join(', ')}>
            {people.slice(0, 4).map((m) => (
              <UserBubble key={m} users={users} email={m} title="" />
            ))}
            {people.length > 4 && <span className="tl-members-more">+{people.length - 4}</span>}
          </span>
        )}
      </header>

      {items.length > 0 && (
        <div className="tl-progress" title={`${progress}% done`}>
          <div className="tl-progress-bar"><span style={{ width: `${progress}%` }} /></div>
          <span className="tl-progress-text">{progress}%</span>
        </div>
      )}

      {(next || late > 0) && (
        <div className="tp-card-next">
          {next && (
            <span className="tk-chip tk-chip-week">
              <TaskIcon name={next.item.node_kind === 'event' ? 'event' : 'milestone'} size={11} />
              {next.item.text || 'Untitled'} {inDaysLabel(next.inDays)}
            </span>
          )}
          {late > 0 && (
            <span className="tk-chip tk-chip-danger">
              <TaskIcon name="critical" size={11} />
              {late} late
            </span>
          )}
        </div>
      )}

      {branches.length ? (
        <ul className="tp-card-branches">
          {branches.slice(0, SHOW_BRANCHES).map((b) => {
            const under = (kids.get(b.id) || []).length;
            return (
              <li key={b.id} className={b.progress >= 100 ? 'is-done' : ''}>
                <ProgressCheck value={b.progress} label={b.text} size={16} disabled />
                <span className="tp-card-branch">{b.text || 'Topic'}</span>
                {under > 0 && <span className="tk-muted">{under}</span>}
              </li>
            );
          })}
          {branches.length > SHOW_BRANCHES && <li className="tk-muted tp-card-more">+{branches.length - SHOW_BRANCHES} more branches</li>}
        </ul>
      ) : (
        <p className="tl-empty">{archived ? 'Nothing in it.' : 'Empty — open it to start the map.'}</p>
      )}

      <footer className="tp-card-foot">
        <span>
          {open.length} open{mine > 0 && <> · <strong>{mine} yours</strong></>}
        </span>
        {archived && onRestore && project.owner_email === me && (
          <button type="button" className="tk-btn tk-btn-ghost tk-btn-sm" onClick={onRestore}>
            <TaskIcon name="unarchive" size={13} /> Restore
          </button>
        )}
      </footer>
    </section>
  );
};

export default ProjectCard;
